import type { CreatureAttack } from "@eldrun/content";

/** O que o golpe precisa do clipe: a duração e o instante do impacto (s), do módulo gerado. */
export interface StrikeClip {
  readonly duration: number;
  readonly impact?: number;
}

/** O que chega do servidor (`CreatureAttackEvent`). */
export interface StrikeEvent {
  readonly attack: string;
  /** Tempo até o acerto quando o servidor mandou (ms): o preparo, mais o voo ou a descida. */
  readonly inMs: number;
  /** Marca de um ataque de várias (0 é a primeira). */
  readonly mark?: number;
  /** Tempo a mais em que a área segue batendo (pulsos), ms. */
  readonly activeMs?: number;
}

/** Faixa da velocidade do clipe: mais rápido que 2,5× vira borrão; mais lento que 0,5×, câmera lenta. */
export const STRIKE_RATE = { min: 0.5, max: 2.5 } as const;

/** O quadro do impacto fica pelo menos isto (ms) depois do acerto (o mergulho acaba no impacto). */
const IMPACT_HOLD_MS = 150;

/**
 * Instante (ms, relógio do cliente) em que o quadro do impacto do clipe tem de aparecer, para o golpe
 * do ataque `def` anunciado agora (`now`) com `inMs` até o acerto e `rtt` de ida e volta. O acerto do
 * servidor cai em `agora + inMs − rtt/2` (a mesma conta do aviso no chão, `Telegraphs.spawn`):
 * - golpe, agarrar, salto e carga: o impacto é o acerto (o fim do preparo; no salto e na carga, o
 *   começo do deslocamento que atravessa a faixa);
 * - mergulho: o acerto é o pouso (o `inMs` já tem a descida);
 * - marcas: a primeira marca (as outras não mexem no clipe);
 * - arremesso: o impacto do clipe é a mão soltando o caldeirão, no fim do preparo (o acerto menos o voo).
 * `undefined` quando o evento não mexe no clipe (marcas depois da primeira).
 */
export function strikeImpactAt(def: Pick<CreatureAttack, "kind" | "windup">, event: StrikeEvent, now: number, rtt: number): number | undefined {
  if (def.kind === "marcas" && (event.mark ?? 0) > 0) return undefined;
  const lag = rtt / 2;
  const hit = now + Math.max(0, event.inMs - lag);
  if (def.kind === "arremesso") return Math.min(hit, now + Math.max(0, def.windup * 1000 - lag));
  return hit;
}

/**
 * O tempo a mais em que o golpe segue batendo depois do impacto (ms): os pulsos que o servidor manda
 * (`activeMs`) ou, nas marcas em fila (os caldeirões da Rainha), o intervalo até a última.
 */
export function strikeActiveMs(def: Pick<CreatureAttack, "kind" | "marks">, event: StrikeEvent): number {
  if (event.activeMs !== undefined) return event.activeMs;
  if (def.kind === "marcas" && def.marks && !def.marks.perPlayer) return Math.max(0, def.marks.count - 1) * def.marks.gap * 1000;
  return 0;
}

/** Um golpe em andamento: o clipe, onde começa e as duas velocidades (até o impacto e depois dele). */
export interface Strike {
  readonly clip: string;
  /** Quando o clipe começa a andar (ms); antes, fica no primeiro quadro (o corpo armando). */
  readonly startAt: number;
  /** Instante do clipe em `startAt` (s): um evento atrasado começa no meio. */
  readonly startTime: number;
  readonly impactAt: number;
  /** Velocidade até o impacto e depois dele. */
  readonly rate: number;
  readonly afterRate: number;
  /** O fim do clipe (ms): depois, a pose volta à locomoção. */
  readonly endAt: number;
  readonly impact: number;
  readonly duration: number;
  /**
   * Toca também fora dos estados de ataque (por cima da locomoção): as marcas anunciadas com a criatura
   * andando, sem preparo no servidor (o cuspe do Dragão no Mergulho).
   */
  readonly free?: boolean;
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/**
 * Plano do golpe: o quadro do impacto do clipe (`clip.impact`) cai em `impactAt`. Até lá, o clipe anda a
 * `impacto / tempo que falta`, limitado a `STRIKE_RATE`: mais rápido que o teto, começa no meio (um
 * evento atrasado também); mais lento que o piso, espera parado no primeiro quadro e começa a tempo.
 * Depois do impacto, o resto do clipe cobre o tempo a mais em que ele bate (`activeMs`) e a
 * recuperação, na mesma faixa de velocidade.
 */
export function planStrike(name: string, clip: StrikeClip, now: number, impactAt: number, recoveryMs: number, activeMs = 0): Strike {
  // O último quadro que a pose mostra é um pouco antes do fim (`creature-state` segura ali): o impacto
  // no fim do clipe (o mergulho da ave) cai nele.
  const impact = clamp(clip.impact ?? clip.duration * 0.5, 0, clip.duration - 1e-3);
  const tail = clip.duration - impact;
  const afterRate = clamp(tail / Math.max(1e-3, (activeMs + recoveryMs) / 1000), STRIKE_RATE.min, STRIKE_RATE.max);
  const remaining = (impactAt - now) / 1000;
  if (remaining <= 0) {
    // Atrasado: o impacto já passou; o clipe entra depois dele, onde estaria agora.
    const startTime = Math.min(clip.duration, impact - remaining * afterRate);
    return { clip: name, startAt: now, startTime, impactAt, rate: afterRate, afterRate, endAt: now + ((clip.duration - startTime) / afterRate) * 1000, impact, duration: clip.duration };
  }
  const rate = impact > 0 ? clamp(impact / remaining, STRIKE_RATE.min, STRIKE_RATE.max) : STRIKE_RATE.max;
  // Onde o clipe estaria agora para chegar ao impacto a tempo: abaixo de 0, espera; acima, começa ali.
  const start = impact - rate * remaining;
  const startAt = start < 0 ? now + (-start / rate) * 1000 : now;
  return { clip: name, startAt, startTime: Math.max(0, start), impactAt, rate, afterRate, endAt: impactAt + Math.max(IMPACT_HOLD_MS, (tail / afterRate) * 1000), impact, duration: clip.duration };
}

/** Instante do clipe em `now` (s), ou `undefined` depois do fim. Antes do começo, o primeiro quadro. */
export function strikeTime(strike: Strike, now: number): number | undefined {
  if (now >= strike.endAt) return undefined;
  if (now <= strike.startAt) return strike.startTime;
  if (now <= strike.impactAt) return Math.min(strike.impact, strike.startTime + ((now - strike.startAt) / 1000) * strike.rate);
  // Depois do impacto (o evento atrasado já começou depois dele: `startTime` ≥ impacto).
  const from = Math.max(strike.impactAt, strike.startAt);
  const base = strike.startAt > strike.impactAt ? strike.startTime : strike.impact;
  return Math.min(strike.duration, base + ((now - from) / 1000) * strike.afterRate);
}
